import React, { useMemo, useState } from 'react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Box,
  Input,
  Checkbox,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';

export default function MultiSelectDropdown({
  label = 'Select',
  options = [],
  selectedValues = [],
  onToggle,
  placeholder = 'Select',
  isDisabled = false,
  maxHeight = '300px',
  systemSelect = false,
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [search, setSearch] = useState('');
  const optionBgSelected = useColorModeValue('blue.50', 'blue.900');
  const optionBgHover = useColorModeValue('blue.100', 'blue.800');
  const optionTextColor = useColorModeValue('gray.800', 'gray.100');

  const normalizedOptions = options.map((opt) =>
    typeof opt === 'string' ? { label: opt, value: opt } : opt,
  );

  const filteredOptions = normalizedOptions.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase()),
  );

  const sortedOptions = useMemo(() => {
    return [...filteredOptions].sort((a, b) => {
      const aSel = selectedValues.includes(a.value);
      const bSel = selectedValues.includes(b.value);
      if (aSel === bSel) return 0;
      return aSel ? -1 : 1;
    });
  }, [filteredOptions, selectedValues]);

  return (
    <Menu
      isLazy
      closeOnSelect={false}
      strategy="fixed"
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={() => {
        setSearch('');
        onClose();
      }}
    >
      <MenuButton
        as={Button}
        w="100%"
        borderRadius={'8px'}
        textAlign="left"
        variant="outline"
        fontWeight="normal"
        rightIcon={
          <Box
            transition="transform 0.25s ease"
            transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
          >
            <ChevronDownIcon />
          </Box>
        }
        isDisabled={isDisabled}
      >
        {selectedValues.length
          ? `${selectedValues.length} selected`
          : placeholder}
      </MenuButton>

      <MenuList maxH={maxHeight} overflowY="auto" p={2} zIndex="popover">
        <Box mb={2}>
          <Input
            size="sm"
            placeholder={`Search ${label.toLowerCase()}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </Box>

        {sortedOptions.length === 0 && (
          <MenuItem isDisabled>No result found</MenuItem>
        )}

        {sortedOptions.map((opt) => {
          const isSelected = selectedValues.includes(opt.value);

          return (
            <MenuItem
              key={opt.value}
              closeOnSelect={false}
              bg={isSelected ? optionBgSelected : 'transparent'}
              color={optionTextColor}
              borderRadius="md"
              _hover={{
                bg: optionBgHover,
                color: optionTextColor,
              }}
              _focus={{
                bg: optionBgHover,
              }}
              onClick={() => {
                onToggle(opt.value);
                systemSelect && onClose();
              }}
            >
              <Checkbox
                isChecked={isSelected}
                pointerEvents="none"
                colorScheme="blue"
              >
                {opt.label}
              </Checkbox>
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
}
